import { dayjs } from '@/utils/dayjs';
import {
  IPermitTOTPayment,
  ReportingDetail,
  TTaxCollection,
} from 'deckard-ui/dist/components/TaxRemittanceViewer/types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { TOTPayment } from '../types';

type RevenueConstructor = (
  payment: TOTPayment,
  reportingLineItems?: ReportingDetail[]
) => IPermitTOTPayment['revenue'];

const constructRevenueFromPayment: RevenueConstructor = (
  payment,
  reportingLineItems = []
) => {
  const customFields: Record<string, unknown> = Object.fromEntries(
    reportingLineItems.map((revenueItem) => [
      revenueItem.key,
      get(payment, revenueItem.key),
    ])
  );

  const fields = {
    taxableReceipts: payment.taxableReceipts,
    allottedCredit: payment.credit,
  };

  return [Object.assign({}, customFields, fields)];
};

const constructRevenueFromTaxableActivities: RevenueConstructor = (
  payment,
  reportingLineItems = []
) => {
  return payment.taxableActivities.map((taxActivity) => {
    const customFields = Object.fromEntries(
      reportingLineItems.map((revenueItem) => [
        revenueItem.key,
        get(taxActivity, revenueItem.key),
      ])
    );

    const provider =
      typeof taxActivity.provider === 'string' && taxActivity.provider.length
        ? taxActivity.provider
        : undefined;

    const fields = {
      provider,
      // Casting for now due to the definition of the TaxableActivity type within lib-deckardui.
      totalRevenue: taxActivity.totalRevenue as number,
      // Casting for now due to the definition of the TaxableActivity type within lib-deckardui.
      taxableReceipts: taxActivity.taxableReceipts as number,
      // Casting for now due to the definition of the TaxableActivity type within lib-deckardui.
      allowableDeductions: taxActivity.allowableDeductions as number,
    };

    return Object.assign({}, customFields, fields);
  });
};

/**
 * This function repurposes relevant pieces of remittance data to generate a tax collection.
 * Useful for places like Rowlett or Independence where this is a single tax collected, but not
 * recorded within the taxable activities structure.
 */
function createTaxCollectionFromPayment(
  payment: TOTPayment,
  totAlias: string
): TTaxCollection {
  return {
    [totAlias]: {
      total: payment.subtotal,
      totDue: payment.totDue,
      latePenalties: payment.latePenalties,
      accruedInterest: payment.accruedInterest,
      ...(typeof payment.credit === 'number' && { credit: payment.credit }),
    },
  };
}

type RemittanceConstructor = (
  payment: TOTPayment,
  totAlias: string
) => IPermitTOTPayment['remittances'];

const constructRemittanceFromPayment: RemittanceConstructor = (
  payment,
  totAlias
) => [
  {
    taxCollection: createTaxCollectionFromPayment(payment, totAlias),
  },
];

const constructRemittanceFromTaxableActivities: RemittanceConstructor = (
  payment,
  totAlias
) => {
  /**
   * This is to prevent duplicate data for jurisdictions that:
   *  1. categorize revenue by providers
   *  2. partially leverage the taxable activity field
   *  3. does not record individual tax specifics
   *
   * e.g. Galveston, Sacramento, and Rowlett
   */
  const taxTypesDoesNotExist = payment.taxableActivities.every(
    (taxActivity) =>
      Object.is(taxActivity.taxTypes, undefined) ||
      Object.is(taxActivity.taxTypes, null)
  );

  if (taxTypesDoesNotExist) {
    return [
      { taxCollection: createTaxCollectionFromPayment(payment, totAlias) },
    ];
  }

  return payment.taxableActivities.map((taxActivity) => {
    const taxCollection =
      Object.is(taxActivity.taxTypes, undefined) ||
      Object.is(taxActivity.taxTypes, null)
        ? createTaxCollectionFromPayment(payment, totAlias)
        : taxActivity.taxTypes;

    const provider =
      typeof taxActivity.provider === 'string' && taxActivity.provider.length
        ? taxActivity.provider
        : undefined;

    return {
      provider,
      taxCollection,
    };
  });
};

export function processPermitTOTPayment(
  timezone: string,
  paymentData?: TOTPayment,
  totAlias: string = 'TOT',
  reportingLineItems?: ReportingDetail[]
): IPermitTOTPayment | undefined {
  if (
    Object.is(paymentData, undefined) ||
    Object.is(paymentData, null) ||
    isEmpty(paymentData)
  )
    return undefined;

  const constructedTransactionData: IPermitTOTPayment['transaction'] = {
    id: paymentData.paymentId,
    /**
     * This is the only date string field that is received as an ISO string.
     * However, this date string does not contain the timezone, but it's UTC.
     */
    date: dayjs.utc(paymentData.paymentDate).tz(timezone).format('YYYY-MM-DD'),
    successful: paymentData.paymentSuccessful && !paymentData.isPending,
    pending: paymentData.isPending && !paymentData.paymentSuccessful,
    method: paymentData.paymentProvider,
    payee: paymentData.reporter,
    taxableReceipts: paymentData.taxableReceipts,
    amountPaid: paymentData.paymentTotal,
    totalProcessingFees: paymentData.fractionalPaymentProcessingFee,
    grandTotal: paymentData.fractionalPaymentTotal,
  };

  const constructedReportingData: IPermitTOTPayment['reporting'] = {
    numDaysAvailable: paymentData.daysAvailable,
    numDaysOccupied: paymentData.daysOccupied,
  };

  const emptyTaxableActivities = paymentData.taxableActivities.length === 0;

  const revenueConstructor = emptyTaxableActivities
    ? constructRevenueFromPayment
    : constructRevenueFromTaxableActivities;
  const constructedRevenue: IPermitTOTPayment['revenue'] = revenueConstructor(
    paymentData,
    reportingLineItems
  );

  const remittanceConstructor = emptyTaxableActivities
    ? constructRemittanceFromPayment
    : constructRemittanceFromTaxableActivities;
  const constructedRemittances: IPermitTOTPayment['remittances'] =
    remittanceConstructor(paymentData, totAlias);

  return {
    transaction: constructedTransactionData,
    reporting: constructedReportingData,
    revenue: constructedRevenue,
    remittances: constructedRemittances,
  };
}
